import React, { useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import Button from 'components/esaad-card/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { saveEsaadCardData } from 'services';
import useLoader from 'hooks/useLoader';
import { ROUTES } from 'constants';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import Captcha from 'components/esaad-card/captcha';
import MobileNumberField from './mobileNo-field';

const LinkEsaadCard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { showLoader, hideLoader } = useLoader();
  const [shareId, setShareId] = useState('');
  const [emiratesId, setEmiratesId] = useState('');
  const [mobileNumber, setMobileNumber] = useState('+971');
  const [captcha, setCaptcha] = useState('');
  const [generatedCaptcha, setGeneratedCaptcha] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const share_id = params.get('share_id');
    if (share_id) {
      setShareId(share_id);
    }
  }, [params]);

  const allInputsValid = () => {
    const errs = {};
    if (shareId === '') {
      errs.shareId = { required: true };
    }
    if (emiratesId === '') {
      errs.emiratesId = { required: true };
    } else {
      const valWithoutSpecChars = emiratesId.replace(/-/g, '').replace(/_/g, '');
      if (valWithoutSpecChars.length !== 15) {
        errs.emiratesId = { correct: true };
      }
    }
    if (mobileNumber === '') {
      errs.mobileNumber = { required: true };
    } else {    
      if (!mobileNumber.match(/^\+971([0-9]{9})+$/)) {
        errs.mobileNumber = { correct: true }; 
      }
    }
    if (captcha === '') {
      errs.captcha = { required: true };
    } else if (generatedCaptcha?.toLowerCase() !== captcha?.toLowerCase()) {
      errs.captcha = { notMatching: true };
    }

    setErrors(errs);
    if (Object.values(errs).length) {
      return false;
    }
    return true;
  };

  const onLinkCard = () => {
    if (!allInputsValid()) {
      return false;
    }
    const data = {
      emirateId: emiratesId,
      phoneNumber: `${mobileNumber.replace(/\s/g, '')}`,
      type: 'share'
    };
    const token = params.get('token');
    showLoader();
    saveEsaadCardData(shareId, token, data)
      .then((res) => {
        hideLoader();
        if (res?.data?.result) {
          navigate(ROUTES.ESAAD_CARD.OTP_VERIFY, {
            state: {
              token,
              referenceId: res?.data?.data?.uuid,
              mobileNumber: `${mobileNumber.replace(/\s/g, '')}`,
              shareId,
              emiratesId
            }
          });
        }
      })
      .catch((e) => {
        hideLoader();
        Swal.fire('Error!', e?.response?.data?.meta?.message || e?.response?.data?.error?.message, 'error');
      });
  };

  return (
    <div className="mt-4">
      <p className="text-center text-light fw-bold mb-0" style={{ fontSize: 20 }}>
        {t('LANDING_PAGE.WELCOME')}
      </p>
      <p className="mt-2 px-5 text-center text-light fw-normal mb-0" style={{ fontSize: 12 }}>
        {t('LANDING_PAGE.DESC')}
      </p>
      <div className="mt-4 row">
        <div className="col-md-6">
          <div className="form-group" style={{ marginTop: 12 }}>
            <label
              className="form-label text-light fw-normal"
              style={{ marginBottom: 4, fontSize: 14 }}
              htmlFor="shareid">
              {t('LANDING_PAGE.SHARE_ID_LABEL')} *
            </label>
            <input
              type="text"
              className="form-control bg-light"
              id="shareid"
              value={shareId}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  setErrors({ ...errors, shareId: { required: true } });
                } else {
                  setErrors({ ...errors, shareId: {} });
                }
                setShareId(val);
              }}
              placeholder="SHARE ID"
              style={{ borderColor: errors?.shareId?.required ? 'red' : 'transparent' }}
            />
            {errors?.shareId?.required && (
              <span className="text-light" style={{ fontSize: 14 }}>
                {t('MESSAGES.PLZ_ENTER_SHARE_ID')}
              </span>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group" style={{ marginTop: 12 }}>
            <label
              className="form-label text-light fw-normal"
              style={{ marginBottom: 4, fontSize: 14 }}
              htmlFor="emiratesId">
              {t('LANDING_PAGE.EMIRATES_ID_LABEL')} *
            </label>
            <InputMask
              mask="999-9999-9999999-9"
              value={emiratesId}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  setErrors({ ...errors, emiratesId: { required: true } });
                } else {
                  const valWithoutSpecChars = val.replace(/-/g, '').replace(/_/g, '');
                  if (valWithoutSpecChars.length === 15) {
                    setErrors({ ...errors, emiratesId: {} });
                  } else {
                    setErrors({ ...errors, emiratesId: { correct: true } });
                  }
                }
                setEmiratesId(val);
              }}>
              {(inputProps) => (
                <input
                  {...inputProps}
                  type="text"
                  className="form-control bg-light"
                  id="emiratesId"
                  placeholder="Example: 784-1234-5678911-1"
                  style={{ borderColor: errors?.emiratesId?.required || errors?.emiratesId?.correct ? 'red' : 'transparent' }}
                />
              )}
            </InputMask>
            {errors?.emiratesId?.required && (
              <span className="text-light" style={{ fontSize: 14 }}>
                {t('MESSAGES.PLZ_ENTER_EMIRATES_ID')}
              </span>
            )}
            {errors?.emiratesId?.correct && (
              <span className="text-light" style={{ fontSize: 14 }}>
                {t('MESSAGES.PLZ_ENTER_CORRECT_EMIRATES_ID')}
              </span>
            )}
          </div>
        </div>
        <div className="col-md-6 d-md-none">
          <MobileNumberField
            errors={errors}
            setErrors={setErrors}
            setMobileNumber={setMobileNumber}
            mobileNumber={mobileNumber}
          />
        </div>
        <div className="col-md-6">
          <div className="form-group" style={{ marginTop: 12 }}>
            <label
              className="form-label text-light fw-normal"
              style={{ marginBottom: 4, fontSize: 14 }}
              htmlFor="captcha">
              {t('LANDING_PAGE.CAPTCHA_INPUT_LABEL')} *
            </label>
            <input
              type="text"
              className="form-control bg-light"
              id="captcha"
              value={captcha}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  setErrors({ ...errors, captcha: { required: true } });
                } else if (val?.toLowerCase() !== generatedCaptcha?.toLowerCase()) {
                  setErrors({ ...errors, captcha: { notMatching: true } });
                } else {
                  setErrors({ ...errors, captcha: {} });
                }
                setCaptcha(val);
              }}
              placeholder="Captcha"
              style={{
                borderColor:
                  errors?.captcha?.required || errors?.captcha?.notMatching ? 'red' : 'transparent'
              }}
            />
            {errors?.captcha?.required && (
              <span className="text-light" style={{ fontSize: 14 }}>
                {t('MESSAGES.PLZ_ENTER_CAPTCHA')}
              </span>
            )}
            {errors?.captcha?.notMatching && (
              <span className="text-light" style={{ fontSize: 14 }}>
                {t('MESSAGES.PLZ_ENTER_CORRECT_CAPTCHA')}
              </span>
            )}
          </div>
        </div>
        <div className="col-md-6 d-none d-sm-none d-md-block">
          <MobileNumberField
            errors={errors}
            setErrors={setErrors}
            setMobileNumber={setMobileNumber}
            mobileNumber={mobileNumber}
          />
        </div>
      </div>
      <div className="row" style={{ marginTop: 22 }}>
        <div className="col-md-6">
          <Captcha setCaptcha={setGeneratedCaptcha} />
        </div>
      </div>
      <div className="row" style={{ marginTop: 22 }}>
        <div className="col-md-12">
          <Button
            title={t('LANDING_PAGE.LINK_CARD')}
            backgroundColor="#B4975A"
            className="text-light fw-bold"
            onClick={onLinkCard}
          />
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-md-12">
          <p className="text-light fw-bold mb-0" style={{ fontSize: 12 }}>
            {t('TERMS_AND_CONDITIONS.TITLE')}
          </p>
          <ol className="m-0 mt-2 text-light" style={{ paddingLeft: '1rem', fontSize: 12 }}>
            <li>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur ut elit elit.
            </li>
            <li>
              Curabitur at dignissim justo. Nam iaculis in sem eget condimentum. Etiam congue id
              nibh non luctus. Aenean congue dapibus purus et gravida.
            </li>
            <li>
              Aliquam erat volutpat. Aliquam finibus, turpis at vulputate tincidunt, dui nisl
              venenatis eros, vel auctor mi erat id sem.
            </li>
            <li>
              Vivamus ac enim vitae orci posuere cursus. Donec id commodo diam. Integer sit amet
              dapibus massa. Ut eleifend ante et nunc mollis, vitae mollis lacus mollis.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default LinkEsaadCard;
